import React, { useLayoutEffect, useRef } from 'react';

import { Badge, Nav, NavDropdown, Tab } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { useSuperState } from 'hooks';

import 'components/GMetrixTabs/GMetrixTabs.scss';

type GMetrixTabsTabProps = {
    children?: React.ReactNode;
    disabled?: boolean;
    tabPaneClassName?: string;
    title: React.ReactNode;
};

/**
 * A single tab declaration. Used as a child of GMetrixTabs.
 * This component does not render anything itself — GMetrixTabs reads its props
 * and renders the corresponding React Bootstrap Tab.
 */
export const GMetrixTabsTab = (_props: GMetrixTabsTabProps): null => null;

type GMetrixTabsProps = {
    children: React.ReactNode;
    className?: string;

    // Optional content rendered directly above the tab bar. When stickyHeader is set it
    // sticks together with the tab bar, so callers can pass a page header/toolbar here.
    header?: React.ReactNode;
    onSelect?: (index: number) => void;
    selected: number;

    // When true, the header + tab bar stick to the top of the scroll container while the
    // active tab's content scrolls beneath them.
    stickyHeader?: boolean;

    // When true, only the active tab's content is mounted: panes mount when
    // first activated and unmount when left. Opt-in because it resets per-tab
    // state (search terms, filters, loaded data) on switch. Useful when a tab's
    // content "dials out" into a shared page header, so only the active tab
    // contributes.
    unmountOnExit?: boolean;
};

/**
 * GMetrixTabs wraps React Bootstrap's Tab.Container/Nav/Tab.Content with an
 * index-based API matching the Kendo TabStrip interface previously used across
 * the portal.
 *
 * - `selected` is a 0-based index of the active tab.
 * - `onSelect` receives the 0-based index of the clicked tab.
 * - Falsy children (from conditional rendering) are filtered out before
 *   assigning indices, so the numbering stays stable regardless of which
 *   conditional tabs are rendered.
 *
 * Responsive overflow: only the tabs that fit the available width are rendered
 * inline; the remainder collapse into a trailing "More" dropdown whose pill
 * badge shows how many tabs are hidden. As the container widens more tabs
 * surface inline and the badge count shrinks; as it narrows tabs fold back into
 * the dropdown. Widths are read from an invisible measurement row so the natural
 * (uncollapsed) width of every tab — and of the "More" toggle — is always known,
 * and recomputed on container resize via a ResizeObserver.
 */
export const GMetrixTabs = (props: GMetrixTabsProps) => {

    const { children, className, header, onSelect, selected, stickyHeader = false, unmountOnExit = false } = props;

    const navRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLDivElement>(null);

    const tabs = React.Children.toArray(children)
        .filter((child): child is React.ReactElement<GMetrixTabsTabProps> => React.isValidElement(child));

    const [state, setState] = useSuperState({
        visibleCount: tabs.length
    });
    const { visibleCount } = state;

    // Default to tab 0 when selected is undefined (matches Kendo TabStrip's fallback behaviour).
    const activeIndex = selected ?? 0;
    const activeKey = String(activeIndex);

    // Read the natural widths from the invisible measurement row and work out how
    // many leading tabs fit the container, reserving room for the "More" toggle
    // whenever at least one tab has to overflow.
    const recomputeVisibleCount = () => {

        const container = navRef.current;
        const measure = measureRef.current;

        if (!container || !measure) {

            return;

        }

        const available = container.getBoundingClientRect().width;

        if (available === 0) {

            return;

        }

        const itemWidths = Array.from(measure.querySelectorAll<HTMLElement>('[data-tab-measure]'))
            .map(element => element.getBoundingClientRect().width);
        const totalWidth = itemWidths.reduce((sum, width) => sum + width, 0);
        if (totalWidth <= available + 1) {

            setState({ visibleCount: tabs.length });
            return;

        }

        const moreToggle = measure.querySelector<HTMLElement>('[data-more-measure]');
        const moreWidth = moreToggle ? moreToggle.getBoundingClientRect().width : 0;
        const budget = available - moreWidth;

        let usedWidth = 0;
        let count = 0;

        for (const width of itemWidths) {

            if (usedWidth + width > budget) {

                break;

            }
            usedWidth += width;
            count++;

        }

        setState({ visibleCount: count });

    };

    // Recompute before paint on mount and whenever the tab set changes, and keep
    // it in sync with container width changes via a ResizeObserver.
    useLayoutEffect(() => {

        recomputeVisibleCount();

        const container = navRef.current;

        if (!container || typeof ResizeObserver === 'undefined') {

            return;

        }

        const observer = new ResizeObserver(() => recomputeVisibleCount());
        observer.observe(container);

        return () => observer.disconnect();

    }, [tabs.length, activeIndex]); // eslint-disable-line react-hooks/exhaustive-deps

    const safeVisibleCount = Math.min(visibleCount, tabs.length);
    const visibleTabs = tabs.slice(0, safeVisibleCount);
    const overflowTabs = tabs.slice(safeVisibleCount);

    return (
        <Tab.Container
            activeKey={activeKey}
            onSelect={key => key !== null && onSelect?.(Number(key))}
            transition={false}
        >
            <div className={className}>

                <div
                    className={stickyHeader ? 'gmetrix-tabs-sticky-header' : undefined}

                    // Opaque page-coloured background so scrolled content is hidden behind the
                    // pinned header/tab bar (and blends with the page rather than showing a box).
                    style={stickyHeader ? { backgroundColor: '#f2f5fa', position: 'sticky', top: 0, zIndex: 1020 } : undefined}
                >

                    {header}

                    <div className={'position-relative'} ref={navRef}>

                        <Nav className={'flex-nowrap'} variant={'tabs'}>
                            {visibleTabs.map(child => {

                                const index = tabs.indexOf(child);

                                return (
                                    <Nav.Item className={'flex-shrink-0'} key={index}>
                                        <Nav.Link className={'text-nowrap'} disabled={child.props.disabled} eventKey={String(index)}>
                                            {child.props.title}
                                        </Nav.Link>
                                    </Nav.Item>
                                );

                            })}

                            {overflowTabs.length > 0 && (
                            <NavDropdown
                                title={(
                                    <>
                                        <span className={'text-capitalize'}>
                                            <FormattedMessage defaultMessage="more" id="more.42805ef8" />
                                        </span>
                                        <Badge pill bg={'secondary'} className={'ms-1'}>
                                            {overflowTabs.length}
                                        </Badge>
                                    </>
                                )}
                                active={overflowTabs.some(child => tabs.indexOf(child) === activeIndex)}
                                align={'end'}
                                className={'flex-shrink-0'}
                                id={'gmetrix-tabs-more'}
                            >
                                {overflowTabs.map(child => {

                                    const index = tabs.indexOf(child);

                                    return (
                                        <NavDropdown.Item
                                            active={index === activeIndex}
                                            disabled={child.props.disabled}
                                            eventKey={String(index)}
                                            key={index}
                                        >
                                            {child.props.title}
                                        </NavDropdown.Item>
                                    );

                                })}
                            </NavDropdown>
                            )}
                        </Nav>

                        {/* Invisible measurement row: renders every tab (and the "More" toggle)
                        at natural width so overflow can be computed regardless of which tabs
                        are currently collapsed. Kept out of the accessibility tree and after
                        the visible nav in the DOM so querySelector-based tours resolve the
                        real tabs first. */}
                        <div
                            aria-hidden
                            className={'nav nav-tabs flex-nowrap position-absolute top-0 start-0 w-100 overflow-hidden invisible'}
                            ref={measureRef}
                            style={{ pointerEvents: 'none' }}
                        >
                            {tabs.map((child, index) => (
                                <div data-tab-measure className={'nav-item flex-shrink-0'} key={index}>
                                    <span className={`nav-link text-nowrap ${index === activeIndex ? 'active' : ''}`.trim()}>
                                        {child.props.title}
                                    </span>
                                </div>
                            ))}
                            <div className={'nav-item flex-shrink-0'}>
                                <span data-more-measure className={'nav-link dropdown-toggle text-nowrap'}>
                                    <span className={'text-capitalize'}>
                                        <FormattedMessage defaultMessage="more" id="more.42805ef8" />
                                    </span>
                                    <Badge pill bg={'secondary'} className={'ms-1'}>
                                        {tabs.length}
                                    </Badge>
                                </span>
                            </div>
                        </div>

                    </div>

                </div>

                <Tab.Content className={'border border-top-0'}>
                    {tabs.map((child, index) => (
                        <Tab.Pane
                            className={`bg-body p-3 ${child.props.tabPaneClassName ?? ''}`.trim()}
                            eventKey={String(index)}
                            key={index}
                            mountOnEnter={unmountOnExit}
                            unmountOnExit={unmountOnExit}
                        >
                            {child.props.children}
                        </Tab.Pane>
                    ))}
                </Tab.Content>

            </div>
        </Tab.Container>
    );

};
